import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MainContainer } from "../../components/ContentWrapper";
import { PageTitle } from "../../components/PageTitle";
import {
    AM_STATUS_URL,
    DASH_TABS,
    PERIODS,
    TabId,
    sumSegments,
    toExpertsTop,
    toFulfillBot,
    toInternalStatusRows,
    toFulfillMid,
    toFulfillTop,
    toGeneralCards,
    toOpsCards,
    toPipeline,
    toSalesCards,
    toSegments,
} from "./helper";
import {
    CustomRangePopover,
    DonutSection,
    HalfDonutSection,
    MetricCard,
    MetricSkeleton,
    Num,
    PipelineList,
    StageStrip,
    countInFlight,
    SectionHeader,
} from "./components";
import { CommandPalette } from "./CommandPalette";
import { RoadmapTimeline } from "./roadmap/RoadmapTimeline";
import { useRoadmap } from "./roadmap/useRoadmap";
import ActivityFeed from "../../components/ActivityFeed";
import { IconGoToTab } from "../../utils/icons";
import {
    useAreasOfFocus,
    useExpertGeo,
    useExperts,
    useFulfillment,
    useGeneral,
    usePipeline,
    useProductCategories,
    useProductSubCategories,
    useProductSubSubCategories,
    useProfessions,
    useSales,
    useShootLocations,
    useSubProfessions,
} from "./useDashboardData";

const SHOW_SEARCH_BAR = false;
const EASE = "cubic-bezier(.4,0,.2,1)";

const GRID_2 = "grid grid-cols-1 sm:grid-cols-2 gap-3.5 mb-3.5 items-stretch";
const GRID_3 = "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 mb-3.5 items-stretch";
const GRID_4 = "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3.5 items-stretch";

export default function InternalDashboard() {
    const [period, setPeriod] = useState("30d");
    const [activeTab, setActiveTab] = useState<TabId>("overview");
    const [loaded, setLoaded] = useState(false);
    const [customRange, setCustomRange] = useState<{ from: string; to: string } | null>(null);
    const [customAnchor, setCustomAnchor] = useState<HTMLElement | null>(null);
    const [paletteOpen, setPaletteOpen] = useState(false);
    const navigate = useNavigate();
    const goTo = (url: string) => navigate(url);

    useEffect(() => {
        const t = setTimeout(() => setLoaded(true), 100);
        return () => clearTimeout(t);
    }, []);

    useEffect(() => {
        if (!SHOW_SEARCH_BAR) return;
        const handler = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setPaletteOpen(true);
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, []);

    const show = (id: TabId) => activeTab === "overview" || activeTab === id;
    const apiArg = { period: period.toLowerCase(), from: customRange?.from, to: customRange?.to };

    const generalQ = useGeneral(apiArg);
    const pipelineQ = usePipeline(apiArg);
    const expertsQ = useExperts(apiArg);
    const fulfillmentQ = useFulfillment(apiArg);
    const shootLocQ = useShootLocations(apiArg);
    const salesQ = useSales(apiArg);
    const professionsQ = useProfessions(apiArg);
    const subProfessionsQ = useSubProfessions(apiArg);
    const areasOfFocusQ = useAreasOfFocus(apiArg);
    const expertGeoQ = useExpertGeo(apiArg);
    const productCategoriesQ = useProductCategories(apiArg);
    const productSubCategoriesQ = useProductSubCategories(apiArg);
    const productSubSubCategoriesQ = useProductSubSubCategories(apiArg);
    // Only fetches once the Roadmap tab is opened — keeps Jira traffic off
    // normal dashboard loads. Deliberately not part of show()/Overview: the
    // timeline is tall and takes no period args.
    const roadmapQ = useRoadmap(activeTab === "roadmap");

    const periodQuery = useMemo(() => {
        const params = new URLSearchParams({ period: period.toLowerCase() });
        if (customRange) {
            params.set("from", customRange.from);
            params.set("to", customRange.to);
        }
        return params.toString();
    }, [period, customRange]);

    const general = useMemo(() => toGeneralCards(generalQ.data, periodQuery), [generalQ.data, periodQuery]);
    const ops = useMemo(() => toOpsCards(generalQ.data, periodQuery), [generalQ.data, periodQuery]);
    const pipeline = useMemo(() => toPipeline(pipelineQ.data?.rows ?? []), [pipelineQ.data]);
    const expertsTop = useMemo(() => toExpertsTop(expertsQ.data, periodQuery), [expertsQ.data, periodQuery]);
    const professions = useMemo(() => toSegments(professionsQ.data ?? [], ["name", "profession", "label"]), [professionsQ.data]);
    const subProfessions = useMemo(() => toSegments(subProfessionsQ.data ?? [], ["name", "label"]), [subProfessionsQ.data]);
    const areasOfFocus = useMemo(() => toSegments(areasOfFocusQ.data ?? [], ["name", "label"]), [areasOfFocusQ.data]);
    const productCategories = useMemo(() => toSegments(productCategoriesQ.data ?? [], ["name", "label"]), [productCategoriesQ.data]);
    const productSubCategories = useMemo(() => toSegments(productSubCategoriesQ.data ?? [], ["name", "label"]), [productSubCategoriesQ.data]);
    const productSubSubCategories = useMemo(() => toSegments(productSubSubCategoriesQ.data ?? [], ["name", "label"]), [productSubSubCategoriesQ.data]);
    const salesCards = useMemo(() => toSalesCards(salesQ.data, periodQuery), [salesQ.data, periodQuery]);
    const expertGeo = useMemo(() => toSegments(expertGeoQ.data ?? [], ["state", "label", "name"]), [expertGeoQ.data]);
    const fulfillTop = useMemo(() => toFulfillTop(fulfillmentQ.data, AM_STATUS_URL), [fulfillmentQ.data]);
    const fulfillMid = useMemo(() => toFulfillMid(fulfillmentQ.data), [fulfillmentQ.data]);
    const fulfillBot = useMemo(() => toFulfillBot(fulfillmentQ.data), [fulfillmentQ.data]);
    const internalStatusRows = useMemo(() => toInternalStatusRows(fulfillmentQ.data), [fulfillmentQ.data]);
    const internalStatusInFlight = countInFlight(internalStatusRows);

    const professionsTotal = sumSegments(professions);
    const subProfessionsTotal = sumSegments(subProfessions);
    const areasOfFocusTotal = sumSegments(areasOfFocus);
    const productCategoriesTotal = sumSegments(productCategories);
    const productSubCategoriesTotal = sumSegments(productSubCategories);
    const productSubSubCategoriesTotal = sumSegments(productSubSubCategories);
    const expertGeoTotal = sumSegments(expertGeo);
    const waitingConfirmation = expertsQ.data?.waiting_on_confirmation.val ?? 0;
    const waitingConfirmationTarget = expertsQ.data?.waiting_on_confirmation.target ?? 30;
    const shootLocations = shootLocQ.data?.segments ?? [];
    const shootLocationsTotal = shootLocQ.data?.total ?? 0;
    const salesData = salesQ.data;

    const tabCounts: Partial<Record<TabId, number>> = {
        general: general.length + ops.length,
        account: pipeline.length,
        experts: expertsTop.length + (expertsQ.data ? 1 : 0),
        fulfillment: fulfillTop.length + fulfillMid.length + fulfillBot.length + internalStatusRows.length,
        sales: salesCards.length,
        roadmap: roadmapQ.data?.epics.length,
    };

    const allQueries = [
        generalQ, pipelineQ, expertsQ, fulfillmentQ, shootLocQ, salesQ,
        professionsQ, subProfessionsQ, areasOfFocusQ, expertGeoQ,
        productCategoriesQ, productSubCategoriesQ, productSubSubCategoriesQ,
    ];
    const hasError = allQueries.some((q) => q.error);
    const generalLoading = generalQ.loading && !generalQ.data;

    return (
        <MainContainer>
            <div className="box-border max-w-full font-['CircularStd','DM_Sans',sans-serif]">
                <header className="flex items-start justify-between pb-5 gap-4 flex-wrap max-[900px]:flex-col">
                    <div>
                        <PageTitle title="Dashboard" />
                        <p className="text-[13px] text-text-2 mt-1">Key metrics and insights at a glance</p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {SHOW_SEARCH_BAR && (
                            <button
                                type="button"
                                onClick={() => setPaletteOpen(true)}
                                className="h-[38px] box-border flex items-center gap-2 px-3.5 border border-border-token rounded-[9px] bg-surface text-[12.5px] text-muted min-w-[280px] hover:border-accent cursor-pointer text-left max-[600px]:hidden"
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                    <circle cx="11" cy="11" r="8" />
                                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                </svg>
                                <span className="flex-1 text-left">Search…</span>
                            </button>
                        )}
                        <div className="h-[38px] box-border flex bg-surface-2 rounded-[9px] p-1 max-[600px]:hidden">
                            {PERIODS.map((p) => {
                                const isActive = period === p;
                                const isCustom = p === "Custom";
                                const label = isCustom && customRange ? `${customRange.from} – ${customRange.to}` : p;
                                return (
                                    <button
                                        key={p}
                                        onClick={(e) => {
                                            setPeriod(p);
                                            if (isCustom) setCustomAnchor(e.currentTarget);
                                            else setCustomRange(null);
                                        }}
                                        className={`h-full px-3.5 text-[12px] rounded-md flex items-center cursor-pointer transition-all ${
                                            isActive
                                                ? "bg-surface text-text-1 font-semibold shadow-[shadow:var(--sh-1)]"
                                                : "bg-transparent text-text-2 font-medium"
                                        }`}
                                    >
                                        {label}
                                    </button>
                                );
                            })}
                        </div>
                        <CustomRangePopover
                            anchor={customAnchor}
                            onClose={() => setCustomAnchor(null)}
                            onApply={(from, to) => {
                                setCustomRange({ from, to });
                                setCustomAnchor(null);
                            }}
                        />
                    </div>
                </header>

                <div className="inline-flex flex-wrap gap-1 p-1.5 bg-surface border border-border-token rounded-xl mb-2 w-fit max-w-full">
                    {DASH_TABS.map((t) => {
                        const isActive = activeTab === t.id;
                        const count = tabCounts[t.id];
                        return (
                            <button
                                key={t.id}
                                type="button"
                                onClick={() => setActiveTab(t.id)}
                                className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-[13px] font-semibold cursor-pointer transition-all whitespace-nowrap ${
                                    isActive ? "" : "bg-transparent text-text-2 hover:bg-surface-2"
                                }`}
                                style={
                                    isActive
                                        ? t.color.startsWith("var(")
                                            // Neutral tab (Overview): the container is --surface, so the
                                            // active wash has to be --surface-2 to stay visible.
                                            ? { background: "var(--surface-2)", color: "var(--text)" }
                                            // Category tabs: 10% wash of the category hue (hex only).
                                            : { background: `${t.color}1A`, color: t.color }
                                        : undefined
                                }
                            >
                                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: t.color }} />
                                {t.label}
                                {count !== undefined && count > 0 && (
                                    <span
                                        className={`text-[11px] font-semibold text-text-2 px-1.5 py-px rounded min-w-[18px] text-center ${
                                            isActive ? "bg-surface" : "bg-surface-2"
                                        }`}
                                    >
                                        {count}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                {hasError && (
                    <div className="mb-3.5 rounded-[14px] border border-transparent bg-down-bg px-5 py-3.5 text-[13px] font-medium text-down">
                        Some dashboard data could not be loaded. Please refresh to try again.
                    </div>
                )}

                {show("general") && (
                    <>
                        <SectionHeader title="General" desc="Top-line health of the platform" color="#C8A97E" viewAllUrl="/brands" />
                        {generalLoading ? (
                            <>
                                <div className={GRID_3}>
                                    {[0, 1, 2].map((i) => (
                                        <MetricSkeleton key={i} />
                                    ))}
                                </div>
                                <div className={GRID_4}>
                                    {[0, 1, 2, 3].map((i) => (
                                        <MetricSkeleton key={i} />
                                    ))}
                                </div>
                            </>
                        ) : (
                            <>
                                <div className={GRID_3}>
                                    {general.map((g, i) => (
                                        <MetricCard key={i} item={g} index={i} loaded={loaded} onLinkClick={goTo} />
                                    ))}
                                </div>
                                <div className={GRID_4}>
                                    {ops.map((o, i) => (
                                        <MetricCard key={i} item={o} index={i} delay={400} loaded={loaded} onLinkClick={goTo} />
                                    ))}
                                </div>
                                <div className="mb-3.5">
                                    <ActivityFeed
                                        level="global"
                                        title="All portal activity"
                                        filters="full"
                                        syncUrl
                                        maxHeight={280}
                                    />
                                </div>
                            </>
                        )}
                        {(productCategoriesTotal > 0 || productSubCategoriesTotal > 0 || productSubSubCategoriesTotal > 0) && (
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3.5 mb-3.5 items-stretch">
                                {productCategoriesTotal > 0 && (
                                    <DonutSection
                                        bare
                                        title="Product Categories"
                                        desc="Products by top-level category"
                                        segments={productCategories}
                                        total={productCategoriesTotal}
                                        displayValue={productCategories.length}
                                        displayLabel="CATEGORIES"
                                    />
                                )}
                                {productSubCategoriesTotal > 0 && (
                                    <DonutSection
                                        bare
                                        title="Product Sub-Categories"
                                        desc="Products by sub-category"
                                        segments={productSubCategories}
                                        total={productSubCategoriesTotal}
                                        displayValue={productSubCategories.length}
                                        displayLabel="SUB-CATEGORIES"
                                    />
                                )}
                                {productSubSubCategoriesTotal > 0 && (
                                    <DonutSection
                                        bare
                                        title="Product Sub-Sub-Categories"
                                        desc="Products by sub-sub-category"
                                        segments={productSubSubCategories}
                                        total={productSubSubCategoriesTotal}
                                        displayValue={productSubSubCategories.length}
                                        displayLabel="SUB-SUB-CATS"
                                    />
                                )}
                            </div>
                        )}
                    </>
                )}

                {show("account") && (
                    <>
                        <SectionHeader title="Account Management" desc="Pipeline status across the account team" color="#4A90B8" viewAllUrl={AM_STATUS_URL} />
                        <PipelineList rows={pipeline} loaded={loaded} onRowClick={goTo} />
                    </>
                )}

                {show("experts") && (
                    <>
                        <SectionHeader title="Expert Team" desc="Expert roster, proposals and scheduling" color="#5A9E6F" viewAllUrl="/experts" />
                        <div className={GRID_3}>
                            {expertsTop.map((e, i) => (
                                <MetricCard key={i} item={e} index={i} delay={800} loaded={loaded} onLinkClick={goTo} />
                            ))}
                            <div className="relative bg-surface border border-border-token rounded-[14px] px-[22px] py-5 transition-all hover:border-tint min-w-0 overflow-hidden h-full flex flex-col">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="text-[12.5px] font-medium text-text-2">Waiting On Expert Confirmation / Shoot Scheduling</div>
                                    <button
                                        type="button"
                                        title="View Scheduling projects"
                                        onClick={() => goTo("/video-production/projects?status=Scheduling")}
                                        className="flex items-center p-0 bg-transparent border-0 cursor-pointer shrink-0 hover:opacity-70 transition-opacity text-muted"
                                    >
                                        <IconGoToTab width={14} height={14} stroke="currentColor" />
                                    </button>
                                </div>
                                <div className="text-[32px] font-extrabold text-text-1 leading-none tracking-[-0.5px] tabular-nums mb-3 mt-2">
                                    <Num value={waitingConfirmation} />
                                </div>
                                <div className="h-2 bg-track rounded overflow-hidden mb-2">
                                    <div
                                        className="h-full rounded min-w-[6px]"
                                        style={{
                                            width: loaded ? `${Math.min((waitingConfirmation / waitingConfirmationTarget) * 100, 100)}%` : "0%",
                                            background: "#9B59B6",
                                            transition: `width 1s ${EASE}`,
                                            transitionDelay: "1000ms",
                                        }}
                                    />
                                </div>
                                <div className="text-[11.5px] text-text-2">Film Date Scheduling</div>
                            </div>
                        </div>
                        {(professionsTotal > 0 || subProfessionsTotal > 0) && (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5 mb-3.5 items-stretch">
                                {professionsTotal > 0 && (
                                    <DonutSection
                                        bare
                                        title="Professions"
                                        desc="Distribution across active expert roster"
                                        segments={professions}
                                        total={professionsTotal}
                                        displayValue={professions.length}
                                        displayLabel="PROFESSIONS"
                                    />
                                )}
                                {subProfessionsTotal > 0 && (
                                    <DonutSection
                                        bare
                                        title="Sub-Professions"
                                        desc="Distribution across active expert roster"
                                        segments={subProfessions}
                                        total={subProfessionsTotal}
                                        displayValue={subProfessions.length}
                                        displayLabel="SUB-PROFESSIONS"
                                    />
                                )}
                            </div>
                        )}
                        {(areasOfFocusTotal > 0 || expertGeoTotal > 0) && (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5 mb-3.5 items-stretch">
                                {areasOfFocusTotal > 0 && (
                                    <DonutSection
                                        bare
                                        title="Areas of Focus"
                                        desc="Sub-sub-professions across active expert roster"
                                        segments={areasOfFocus}
                                        total={areasOfFocusTotal}
                                        displayValue={areasOfFocus.length}
                                        displayLabel="AREAS OF FOCUS"
                                    />
                                )}
                                {expertGeoTotal > 0 && (
                                    <DonutSection
                                        bare
                                        title="Expert Location Geography"
                                        desc="Top regions by expert count"
                                        segments={expertGeo}
                                        total={expertGeoTotal}
                                        displayValue={expertsQ.data?.total_experts.val ?? expertGeoTotal}
                                        displayLabel="EXPERTS"
                                    />
                                )}
                            </div>
                        )}
                    </>
                )}

                {show("fulfillment") && (
                    <>
                        <SectionHeader title="Fulfillment" desc="Production pipeline from shoot to approval" color="#D85A30" viewAllUrl="/video-production/orders" />
                        <div className={GRID_4}>
                            {fulfillTop.map((f, i) => (
                                <MetricCard key={i} item={f} index={i} delay={1200} loaded={loaded} onLinkClick={goTo} />
                            ))}
                        </div>
                        <div className={GRID_3}>
                            {fulfillMid.map((f, i) => (
                                <MetricCard key={i} item={f} index={i} delay={1400} loaded={loaded} onLinkClick={goTo} />
                            ))}
                        </div>
                        <div className={GRID_2}>
                            {fulfillBot.map((f, i) => (
                                <MetricCard key={i} item={f} index={i} delay={1600} loaded={loaded} onLinkClick={goTo} />
                            ))}
                        </div>
                        {internalStatusRows.length > 0 && (
                            <>
                                <div className="flex items-center gap-3 pt-3 pb-4">
                                    <div className="w-1 h-6 rounded bg-[#D85A30]/40" />
                                    <div>
                                        <div className="text-[14.5px] font-bold text-text-1">Internal Status</div>
                                        <div className="text-[12px] text-text-2 mt-px">
                                            Where every deliverable sits in the post pipeline
                                        </div>
                                    </div>
                                    <div className="ml-auto text-[12px] text-text-2">
                                        <span className="font-bold text-text-1 tabular-nums">
                                            {internalStatusInFlight.toLocaleString()}
                                        </span>{" "}
                                        in flight
                                    </div>
                                </div>
                                <StageStrip rows={internalStatusRows} loaded={loaded} onRowClick={goTo} />
                            </>
                        )}
                        {shootLocationsTotal > 0 && shootLocations.length > 0 && (
                            <HalfDonutSection
                                title="Shoot Location Geography"
                                desc="Production locations across all projects"
                                segments={shootLocations}
                                total={shootLocationsTotal}
                            />
                        )}
                    </>
                )}

                {show("sales") && (
                    <>
                        <SectionHeader title="Sales" desc="Content mix, channels and campaigns" color="#6C5CE7" viewAllUrl="/admin/pricing" />
                        {salesCards.length > 0 && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 mb-3.5 items-stretch">
                                {salesCards.map((c, i) => (
                                    <MetricCard key={i} item={c} index={i} delay={1800} loaded={loaded} onLinkClick={goTo} />
                                ))}
                            </div>
                        )}
                        <div className="bg-surface border border-border-token rounded-[14px] overflow-hidden mb-3.5">
                            <div className="px-6 py-[22px] flex items-center justify-between bg-[linear-gradient(135deg,#1C7A5A,#2D9CDB)]">
                                <div className="flex-1">
                                    <div className="text-[11.5px] text-white/45 mb-1.5">
                                        Total revenue · MRR add-ons{salesData?.revenue.stub ? " (placeholder)" : ""}
                                    </div>
                                    <div className="flex items-baseline gap-3">
                                        <span className="text-[34px] font-extrabold text-white tracking-[-0.5px]">
                                            {(salesData?.revenue.val ?? 0) >= 1000 ? (
                                                <>
                                                    $<Num value={Math.round((salesData?.revenue.val ?? 0) / 1000)} />k
                                                </>
                                            ) : (
                                                <>
                                                    $<Num value={salesData?.revenue.val ?? 0} />
                                                </>
                                            )}
                                        </span>
                                        <span className="text-[12px] font-semibold text-white/80">
                                            {(salesData?.revenue.change_pct ?? 0) >= 0 ? "↑" : "↓"} {salesData?.revenue.change_pct ?? 0}% vs prev
                                        </span>
                                    </div>
                                </div>
                                <div className="flex items-end gap-[3px] h-[42px]">
                                    {(salesData?.revenue.spark?.length ? salesData.revenue.spark : [40, 55, 50, 65, 58, 72, 68, 80, 75, 90, 85, 100]).map((h, i, arr) => (
                                        <div
                                            key={i}
                                            className="w-[7px] rounded-sm"
                                            style={{
                                                height: `${h}%`,
                                                background: i === arr.length - 1 ? "#FFF" : "rgba(255,255,255,0.22)",
                                                transition: `height 0.6s ${EASE}`,
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                            {[
                                { label: "Content Types", val: salesData?.content_types.val ?? 0, sub: "Active" },
                                { label: "Channels", val: salesData?.channels.val ?? 0, sub: "Active" },
                                { label: "Platforms", val: salesData?.platforms.val ?? 0, sub: "Categories" },
                            ].map((s, i, arr) => (
                                <div key={i} className={`flex items-center px-6 py-3.5 ${i < arr.length - 1 ? "border-b border-border-token" : ""}`}>
                                    <span className="flex-1 text-[14px] text-text-2">{s.label}</span>
                                    <div className="flex items-center gap-2.5">
                                        <span className="text-[20px] font-extrabold text-text-1 tracking-[-0.3px] tabular-nums">
                                            <Num value={s.val} />
                                        </span>
                                        {s.sub && <span className="text-[11.5px] text-text-2">{s.sub}</span>}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="bg-surface border border-border-token rounded-[14px] overflow-hidden mb-3.5">
                            <div className="px-6 pt-[18px] pb-1.5">
                                <div className="text-[13.5px] font-bold text-text-1">Proposal PDP Types</div>
                                <div className="text-[11.5px] text-text-2">PDP channel sub-type picked on proposals this period</div>
                            </div>
                            {[
                                { label: "Retail", val: salesData?.pdp_types?.retail ?? 0 },
                                { label: "D2C", val: salesData?.pdp_types?.d2c ?? 0 },
                                { label: "Amazon", val: salesData?.pdp_types?.amazon ?? 0 },
                            ].map((s, i, arr) => (
                                <div key={i} className={`flex items-center px-6 py-3.5 ${i < arr.length - 1 ? "border-b border-border-token" : ""}`}>
                                    <span className="flex-1 text-[14px] text-text-2">{s.label}</span>
                                    <span className="text-[20px] font-extrabold text-text-1 tracking-[-0.3px] tabular-nums">
                                        <Num value={s.val} />
                                    </span>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {activeTab === "roadmap" && (
                    <>
                        <SectionHeader title="Product Roadmap" desc="TDC epics from Jira" color="#9B59B6" />
                        <RoadmapTimeline
                            data={roadmapQ.data}
                            loading={roadmapQ.loading}
                            error={roadmapQ.error}
                            onRefresh={() => roadmapQ.refetch(true)}
                        />
                    </>
                )}
            </div>
            {SHOW_SEARCH_BAR && <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />}
        </MainContainer>
    );
}
